// Module dependency rules on the client. The database enforces the same rules; this file
// only keeps the checkboxes consistent while someone is ticking them.
import type { Module } from "./types";

/** The modules plus everything they need, directly or indirectly, plus the always-on modules. */
export function closure(keys: Iterable<string>, catalog: Module[]): Set<string> {
  const byKey = new Map(catalog.map((m) => [m.key, m]));
  const out = new Set<string>();
  const stack = [...keys, ...catalog.filter((m) => m.always_on).map((m) => m.key)];
  while (stack.length) {
    const k = stack.pop()!;
    if (out.has(k) || !byKey.has(k)) continue;
    out.add(k);
    stack.push(...byKey.get(k)!.depends_on);
  }
  return out;
}

/** Enabled modules that need this one switched on. */
export function requiredBy(key: string, enabled: Set<string>, catalog: Module[]): Module[] {
  return catalog.filter((m) => enabled.has(m.key) && m.depends_on.includes(key));
}

/** Tick or untick a module, keeping dependencies consistent. Returns the new set. */
export function toggle(key: string, enabled: Set<string>, catalog: Module[]): Set<string> {
  const mod = catalog.find((m) => m.key === key);
  if (!mod || mod.always_on) return enabled;
  if (enabled.has(key)) {
    if (requiredBy(key, enabled, catalog).length > 0) return enabled; // locked
    const next = new Set(enabled);
    next.delete(key);
    return next;
  }
  return new Set([...enabled, ...closure([key], catalog)]);
}

/** Changes needed to go from `current` to `target`, in an order the database accepts. */
export function plan(current: Set<string>, target: Set<string>, catalog: Module[]) {
  const sorted = [...catalog].sort((a, b) => a.sort_order - b.sort_order);
  const switchOn = sorted.filter((m) => target.has(m.key) && !current.has(m.key)).map((m) => m.key);
  const switchOff = sorted
    .filter((m) => current.has(m.key) && !target.has(m.key) && !m.always_on)
    .map((m) => m.key)
    .reverse(); // dependants first
  return { switchOn, switchOff };
}
